import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { canAny } from "../../utils/helpers/can";
import { subscribeAccessDenied, isPermissionMessage } from "../../utils/accessDenied";
import AccessDenied from "../../components/AccessDenied";
import { SOCIETY_PORTAL } from "../routes";

// Gate a route element behind one or more permission codes.
// Mirrors zg-web-portal/Routing/PermissionOnComponent.jsx, but uses the flat
// permission-code array (with wildcard support) from the new RBAC model.
//
// Usage:
//   <Route path="..." element={
//     <PermissionOnComponent
//       permissions={[PERMISSIONS.SYSTEM_MANAGE_ROLES]}
//       component={<Roles />}
//     />
//   } />
//
// `permissions` is an array — access is granted if ANY of the codes match
// (or any wildcard like `module.*` or `*` matches).
//
// Two ways in to the Access Denied page:
//   1. the role holds none of the codes, so the page never renders;
//   2. the page rendered but its API call came back 403 (the sidebar code and
//      the endpoint's own permission can disagree) — previously a toast that
//      faded and left an empty screen behind.
// "/society-portal/finance/credit-notes/create" -> "Credit Notes", so the page
// can name the section without every route having to pass a label.
const SKIP_SEGMENTS = new Set(['society-portal', 'create', 'add', 'edit', 'view', 'board', 'list']);
const moduleFromPath = (pathname) => {
  const parts = String(pathname || '')
    .split('/')
    .filter(Boolean)
    .filter((part) => !SKIP_SEGMENTS.has(part) && !/^\d+$/.test(part) && !/^:/.test(part));
  const last = parts[parts.length - 1];
  if (!last) return '';
  return last
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

const PermissionOnComponent = ({ permissions = [], component, module }) => {
  const location = useLocation();
  const userPermissions = useSelector((s) => s.UserSocietyReducer?.permissions || []);
  const permissionsLoading = useSelector(
    (s) => s.UserSocietyReducer?.permissionsLoading
  );
  const [apiDenied, setApiDenied] = useState(null);

  // A refusal belongs to the page that was open when it arrived, so it is
  // cleared on navigation.
  useEffect(() => {
    setApiDenied(null);
  }, [location.pathname]);

  useEffect(() => subscribeAccessDenied((detail) => setApiDenied(detail)), []);

  // Hold off on the 403 screen until permissions have actually loaded —
  // otherwise a fresh page reload flashes Access Denied before /my-permissions
  // resolves.
  if (permissionsLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  const roleAllows = !permissions.length || canAny(userPermissions, permissions);
  const label = module || moduleFromPath(location.pathname);

  if (!roleAllows) {
    return (
      <AccessDenied
        module={label}
        permissions={permissions}
        homeLink={SOCIETY_PORTAL}
      />
    );
  }

  if (apiDenied) {
    return (
      <AccessDenied
        module={label}
        permissions={permissions}
        // "Insufficient permissions" from the API says nothing useful, so the
        // page's own wording is used; anything more specific is shown as sent.
        message={isPermissionMessage(apiDenied.message) ? undefined : apiDenied.message}
        homeLink={SOCIETY_PORTAL}
        onRetry={() => setApiDenied(null)}
      />
    );
  }

  return <>{component}</>;
};

export default PermissionOnComponent;
